"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.ListMonitorsSchema = exports.ListMonitorFilterSchema = exports.ErrorListMonitorFilterDuplicateColumn = exports.ListMonitorTagsFilterSchema = exports.ListMonitorSeverityFilterSchema = exports.MonitorListOrderBySchema = exports.GetMonitorFilterOptionsSchema = exports.DeleteMonitorSchema = exports.GetMonitorByIdSchema = exports.UpdateMonitorSchema = exports.CreateMonitorSchema = exports.SessionContextSchema = exports.MonitorNotFoundError = void 0;
/** service/types.ts contains the DTOs, schemas, and errors for the MonitorService. */
const zod_1 = require("zod");
const errors_1 = require("../../../errors");
const zod_2 = require("../../../utils/zod");
const types_1 = require("../types");
/** MonitorNotFoundError is thrown when a monitor row doesn't exist in the project. */
class MonitorNotFoundError extends errors_1.EntellicaNotFoundError {
    constructor(monitorId, projectId) {
        super(`Monitor ${monitorId} not found in project ${projectId}`);
    }
}
exports.MonitorNotFoundError = MonitorNotFoundError;
/** SessionContext identifies the authenticated caller. */
exports.SessionContextSchema = zod_1.z.object({
    userId: zod_1.z.string(),
});
/** omitOnWrite are the Monitor fields the service owns and clients can't set. */
const omitOnWrite = {
    createdAt: true,
    updatedAt: true,
    createdBy: true,
    updatedBy: true,
    severity: true,
    severityChangedAt: true,
    alertedAt: true,
    nextRunAt: true,
    lastPublishedAt: true,
    lastClaimedAt: true,
    lastCompletedAt: true,
};
/** CreateMonitorSchema is the input for MonitorService.create. */
exports.CreateMonitorSchema = types_1.MonitorSchema.omit({
    ...omitOnWrite,
    id: true,
})
    .extend({ status: types_1.MonitorWriteStatusSchema.default("ACTIVE") })
    .superRefine(types_1.validateMonitorQuery)
    .superRefine(types_1.validateThresholdOrder)
    .superRefine(types_1.validateAtLeastOneTrigger);
/** UpdateMonitorSchema is the input for MonitorService.update. */
exports.UpdateMonitorSchema = types_1.MonitorSchema.omit(omitOnWrite)
    // status has no default: a default would un-pause a monitor on a form save.
    .extend({ status: types_1.MonitorWriteStatusSchema.optional() })
    .superRefine(types_1.validateMonitorQuery)
    .superRefine(types_1.validateThresholdOrder)
    .superRefine(types_1.validateAtLeastOneTrigger);
/** GetMonitorByIdSchema is the input for MonitorService.getById. */
exports.GetMonitorByIdSchema = zod_1.z.object({
    projectId: zod_1.z.string(),
    id: zod_1.z.string(),
});
/** DeleteMonitorSchema is the input for MonitorService.delete. */
exports.DeleteMonitorSchema = zod_1.z.object({
    projectId: zod_1.z.string(),
    id: zod_1.z.string(),
});
/** GetMonitorFilterOptionsSchema is the input for MonitorService.getFilterOptions. */
exports.GetMonitorFilterOptionsSchema = zod_1.z.object({
    projectId: zod_1.z.string(),
});
/** MonitorListOrderBySchema is the set of columns MonitorService.list can sort by. */
exports.MonitorListOrderBySchema = zod_1.z.enum([
    "name",
    "status",
    "severity",
    "severityChangedAt",
    "alertedAt",
    "createdAt",
    "updatedAt",
]);
/** ListMonitorSeverityFilterSchema is the severity row of a ListMonitorFilter. */
exports.ListMonitorSeverityFilterSchema = zod_1.z.object({
    type: zod_1.z.literal("stringOptions"),
    column: zod_1.z.literal("severity"),
    operator: zod_1.z.enum(["any of", "none of"]),
    value: zod_1.z.array(types_1.MonitorSeveritySchema).min(1),
});
/** ListMonitorTagsFilterSchema is the tags row of a ListMonitorFilter. */
exports.ListMonitorTagsFilterSchema = zod_1.z.object({
    type: zod_1.z.literal("arrayOptions"),
    column: zod_1.z.literal("tags"),
    operator: zod_1.z.enum(["any of", "all of", "none of"]),
    value: zod_1.z.array(zod_1.z.string()),
});
/** ErrorListMonitorFilterDuplicateColumn is the message for a repeated column. */
exports.ErrorListMonitorFilterDuplicateColumn = "Each column may appear at most once in a ListMonitorFilter";
/** ListMonitorFilterSchema is the typed subset of `singleFilter[]` accepted by MonitorService.list. */
exports.ListMonitorFilterSchema = zod_1.z
    .array(zod_1.z.discriminatedUnion("column", [
    exports.ListMonitorSeverityFilterSchema,
    exports.ListMonitorTagsFilterSchema,
]))
    .refine((rows) => new Set(rows.map((r) => r.column)).size === rows.length, {
    message: exports.ErrorListMonitorFilterDuplicateColumn,
});
/** ListMonitorsSchema is the input for MonitorService.list. */
exports.ListMonitorsSchema = zod_1.z.object({
    projectId: zod_1.z.string(),
    orderBy: zod_1.z
        .object({
        column: exports.MonitorListOrderBySchema,
        order: zod_1.z.enum(["ASC", "DESC"]),
    })
        .nullable(),
    filter: exports.ListMonitorFilterSchema.optional(),
    ...zod_2.paginationZod,
});
